'use client';

import { DateTime } from 'luxon';
import React from 'react';
import { toast } from 'react-toastify';
import { DateFormats, convertAreaByUnitToSqm, convertSqmToAreaByUnit, isPresent } from '@farmai-shared/LFUtils';
import { ICrop } from '@stores/Crop';
import i18n, { seedUnitList } from './next-i18n';
import ts from '@mapbox/timespace';
import * as turf from '@turf/turf';
import * as gjv from 'geojson-validation';
import { v4 as uuidv4 } from 'uuid';
import { interpolateRainbow } from 'd3-scale-chromatic';
import { rollbarInstance } from './rollbar';
import { ActivityFormInputData } from '@app/activity/_components/activityForm/ActivityFormInputs';
import { IActivity } from '@stores/Activity';
import { LangKey } from './responseTypes.js';
import { WeatherData } from '@app/_components/weatherModal/month/Graphs.jsx';
import { AgroAPIError } from './agroapi.js';
import { LFDrawMode } from '@app/_components/lf-map/mapTypes.js';
import emailMisspelled, { top100 } from 'email-misspelled';
export const sendGaEvent = (eventName, params = {}) => {
  window.gtag && window.gtag('event', eventName, params);
};
export const displayLatLong = (coordinate: number) => coordinate.toFixed(5);
const dateRegexp = [/[2]/, /\d/, /\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/];
const thDateRegexp = [/[2]/, /[5]/, /\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/];
const dateTimeRegexp = [/[2]/, /\d/, /\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, ' ', /\d/, /\d/, ':', /\d/, /\d/];
const thDateTimeRegexp = [/[2]/, /[5]/, /\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, ' ', /\d/, /\d/, ':', /\d/, /\d/];
export const maskDateTimeRegexp = (lang?: string) => {
  if (lang === 'th') {
    return thDateTimeRegexp;
  }
  return dateTimeRegexp;
};
export const maskDateRegexp = (lang?: string) => {
  if (lang === 'th') {
    return thDateRegexp;
  }
  return dateRegexp;
};
export const formDateRegexp = /^(?:\d{4}\/\d{2}\/\d{2})$/;
export const cropUrl = (name: string) => `${name?.replace(' ', '_').toLowerCase()}.svg`;
export const formatCropName = (crop: ICrop) => `${cropTranslate(crop)} ${crop.genus ? `(${crop.genus})` : ''} - ${varietyTranslate(crop)}`;
export const formatActivityBoardCropName = (crop: {
  name: string;
  genus?: string | null;
  variety: string;
  name_i18n: { [key in LangKey]: string };
  variety_i18n: { [key in LangKey]: string };
}, lang: LangKey) => `${crop.name_i18n[lang || 'en']} ${crop.genus ? `(${crop.genus})` : ''} - ${crop.variety_i18n[lang || 'en']}`;
export const varietyTranslate = (crop: ICrop) => crop.variety;
export const cropTranslate = (crop: ICrop) => crop.name;
const formatCanonicalName = (canonicalName: string | null) => (canonicalName ?? '').split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
export const activityTypeTranslate = (activityType: {
  label?: string;
  value?: string | null;
  canonical_name: string | null;
}) => i18n.t(`activityTypes:${activityType?.canonical_name}.name`, {
  defaultValue: activityType.label ?? formatCanonicalName(activityType?.canonical_name)
});
export const translateList = (list: {
  label: string;
  value: string | null;
  canonical_name: string | null;
}[], listType: 'activityType') => {
  const translateFunc = {
    activityType: activityTypeTranslate,
    variety: varietyTranslate
  }[listType];
  return list.map(e => ({
    ...e,
    label: translateFunc(e)
  })).sort((a, b) => a.label.localeCompare(b.label));
};
export const selectCropQualityUnit = (cropName: string) => {
  switch (cropName.toLowerCase()) {
    case 'rice':
      return 'MOISTURE_PERC';
    case 'cassava':
      return 'STARCH_CONTENT_PERC';
    case 'sweetcorn':
      return 'BRIX_SUGAR_CONTENT_PERC';
    case 'sugarcane':
      return 'BRIX_SUGAR_CONTENT_PERC';
    default:
      return null;
  }
};

// prefix calendar mask with expected year
export const autoPrefixYearOnFocus = (e: React.FocusEvent<HTMLInputElement>, lang: string, setValue: (label: string, value: string) => void, supportTime?: boolean) => {
  const year = lang === 'th' ? DateTime.now().year + 543 : DateTime.now().year;
  const mask = supportTime ? `/__/__ __:__` : `/__/__`;
  const newString = `${year.toString()}${mask}`;
  e.currentTarget.value.length < 1 && setValue('dateOfArrival', newString);
};
export const thousandSeparator = (num?: number | null | string) => {
  if (isPresent(num)) {
    const parts = num.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.');
  }
  return num;
};
export function handlePromise<T>(promise: Promise<T>, successMessage: () => string, setModalVisible?: (boolean) => void): Promise<T> {
  return promise.then(data => {
    toast.success(successMessage);
    setModalVisible && setModalVisible(false);
    return data;
  }).catch(e => {
    handleErrorResponse(e);
    return e;
  });
}
export const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export const isJSON = (value: unknown): boolean => {
  if (value === '' || value === undefined) return true;
  if (typeof value !== 'string') return false;
  try {
    JSON.parse(value);
  } catch (e) {
    return false;
  }
  return true;
};
export const basicSort = (a, b, order?: 'asc') => {
  if (a === b) {
    return 0;
  }

  // null or undefined sort after anything else
  if (a === null || a === undefined) {
    return 1;
  }
  if (b === null || b === undefined) {
    return -1;
  }

  // ascending - lowest sorts first
  if (order && order === 'asc') {
    return a - b;
  }

  // descending - highest sorts first
  return b - a;
};
export const sortLocales = (a, b, order?: string) => {
  // null or undefined sort after anything else
  if (a === null || a === undefined) {
    return 1;
  }
  if (b === null || b === undefined) {
    return -1;
  }

  // ascending - lowest sorts first
  if (order && order === 'asc') {
    return a.localeCompare(b);
  }

  // descending - highest sorts first
  return b.localeCompare(a);
};
export const sortCrops = (cropA: ICrop, cropB: ICrop, order?: string) => {
  // ascending - lowest sorts first
  if (!cropA.name || !cropB.name) return cropA;
  if (order && order === 'asc') {
    if (cropA.name === cropB.name) {
      return cropA.variety.localeCompare(cropB.variety);
    }
    return cropA.name?.localeCompare(cropB.name);
  }

  // descending - highest sorts first
  if (cropA.name === cropB.name) {
    return cropB.variety.localeCompare(cropA.variety);
  }
  return cropB.name?.localeCompare(cropA.name);
};
export const fieldReadOnlyValue = <T,>(value: T): T | string => {
  if (!isPresent(value)) return '-';
  return value;
};
export const numberFormatter = (val: string) => parseFloat(val.toString().replace(new RegExp(/[^0-9.,]/g), '').replace(',', '.'));

// clean imported geojson features
export const cleanFeature = (feature: any, drawMode?: Exclude<LFDrawMode, 'none'>) => {
  let cleanFeature = feature || {};
  const properties = ['name', 'crop', 'planting_date'];
  const expectedGeo = drawMode || 'polygon';

  // check geometry type and flatten to work with current map linestrings
  if (gjv.isGeometryObject(cleanFeature.geometry)) {
    if (cleanFeature.geometry.type === 'MultiPolygon') {
      cleanFeature.geometry = {
        type: 'GeometryCollection',
        geometries: [{
          type: 'Polygon',
          coordinates: cleanFeature.geometry.coordinates[0]
        }]
      };
    }
    if (cleanFeature.geometry.type === 'Polygon') {
      cleanFeature.geometry = {
        type: 'GeometryCollection',
        geometries: [feature.geometry]
      };
    }
  }
  if (!gjv.isGeometryObject(cleanFeature.geometry)) {
    if (expectedGeo === 'polygon') {
      cleanFeature.geometry = {
        type: 'GeometryCollection',
        geometries: [{
          type: 'Polygon',
          coordinates: [[]]
        }]
      };
    }
    if (expectedGeo === 'point') {
      cleanFeature.geometry = {
        type: 'GeometryCollection',
        geometries: [{
          type: 'Point',
          coordinates: []
        }]
      };
    }
  }

  // create properties object if none
  if (!cleanFeature.properties) {
    cleanFeature = {
      ...cleanFeature,
      properties: {}
    };
  }

  // add necessary properties
  properties.forEach(p => {
    if (!cleanFeature.properties[p]) cleanFeature.properties[p] = null;
    if (p === 'name') cleanFeature.properties[p] = cleanFeature.properties[p]?.toString();
  });

  // transform planting_date
  if (cleanFeature.properties.planting_date) {
    cleanFeature.properties.plantingDate = cleanFeature.properties.planting_date;
  }

  // add timezone
  if (cleanFeature.properties.planting_date) {
    const date = DateTime.fromISO(cleanFeature.properties.planting_date, {
      setZone: true
    });
    let tz;
    if (cleanFeature.geometry?.coordinates?.[0]) {
      tz = ts.getFuzzyLocalTimeFromPoint(date, cleanFeature.geometry?.coordinates?.[0])?.tz();
    }
    cleanFeature.properties.timezone = tz || date.zoneName;
  }

  // add timezone if missing
  if (!cleanFeature.properties.timezone) {
    let tz;
    if (cleanFeature.geometry?.coordinates?.[0]) {
      tz = ts.getFuzzyLocalTimeFromPoint(Date.now(), cleanFeature.geometry?.coordinates?.[0])?.tz();
    }
    cleanFeature.properties.timezone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  // add id
  const id = uuidv4();
  cleanFeature.properties.id = id;
  cleanFeature.id = id;
  return cleanFeature;
};
const isValidCoord = (coord: number[]) => {
  if (coord[0] < -180 || coord[0] > 180 || coord[1] < -90 || coord[1] > 90) {
    return false;
  }
  return true;
};
export const isValidPolygon = (poly: any) => {
  if (!isPresent(poly)) return false;
  try {
    turf.polygon(poly);
  } catch (e) {
    return false;
  }
  return !poly.flat().map(c => isValidCoord(c)).includes(false);
};
export const isMac = () => typeof window !== 'undefined' ? navigator.userAgent.includes('Mac') : false;
export const chunkArray = (array: any[], chunkSize: number) => Array.from({
  length: Math.ceil(array.length / chunkSize)
}, (_, index) => array.slice(index * chunkSize, (index + 1) * chunkSize));
type GroupByResult<K extends PropertyKey, V> = Partial<Record<K, V[]>>;
export const groupBy = <K extends PropertyKey, V>(array: readonly V[], getKey: (cur: V, idx: number, src: readonly V[]) => K): GroupByResult<K, V> => array.reduce((obj, cur, idx, src) => {
  const key = getKey(cur, idx, src);
  (obj[key] || (obj[key] = [])).push(cur);
  return obj;
}, {} as GroupByResult<K, V>);
export const handleErrorResponse = (error: AgroAPIError) => {
  let m = i18n.t('something_happened');
  if (error.response?.data.code) {
    m = i18n.t(error.response.data.code, {
      defaultValue: i18n.t('something_happened')
    });
  }
  if (error.code === '500') {
    rollbarInstance.error(error);
  }
  return toast.error(m);
};
export const formatFertilizationExtraForAPI = (data, unit) => {
  let hasValue = false;
  const fert: {
    type?: string;
    method?: string;
    amount?: number;
    amount_unit?: string;
    nitrogen?: number;
    phosphorus?: number;
    potassium?: number;
  } = {};
  const {
    fertilizerType,
    fertilizerMethod,
    fertilizerAmount,
    fertilizerAmount_unit,
    nitrogen,
    phosphorus,
    potassium
  } = data;
  if (isPresent(fertilizerType?.value)) {
    fert.type = fertilizerType.value;
    hasValue = true;
  }
  if (isPresent(fertilizerMethod?.value)) {
    fert.method = fertilizerMethod.value;
    hasValue = true;
  }
  if (isPresent(fertilizerAmount)) {
    fert.amount = convertAreaByUnitToSqm(fertilizerAmount, unit);
    fert.amount_unit = `${fertilizerAmount_unit.value}/m2`;
    hasValue = true;
  }
  if (isPresent(nitrogen)) {
    fert.nitrogen = parseInt(nitrogen);
    hasValue = true;
  }
  if (isPresent(phosphorus)) {
    fert.phosphorus = parseInt(phosphorus);
    hasValue = true;
  }
  if (isPresent(potassium)) {
    fert.potassium = parseInt(potassium);
    hasValue = true;
  }
  return hasValue ? {
    fertilizers: [fert]
  } : {};
};
export const interpolateColors = (data?: {
  name: string;
}[], colorRangeInfo = {
  colorStart: 0,
  colorEnd: 1
}) => {
  if (!data) return null;
  const {
    colorStart,
    colorEnd
  } = colorRangeInfo;
  const colorRange = colorEnd - colorStart;
  const intervalSize = colorRange / data.length;
  const colors = data.reduce((acc, node, i) => {
    acc[node.name] = interpolateRainbow(colorStart + i * intervalSize);
    return acc;
  }, {});
  return colors;
};
export const formatActivityExtraForAPI = (data: ActivityFormInputData, unit, reverseWeightUnitFactor) => ({
  extra: {
    ...(data.activityType?.canonical_name === 'planting' && {
      ...(isPresent(data.seed_usage) && {
        seed_usage: Number(data.seed_usage)
      }),
      ...(isPresent(data.seed_usage_unit) && {
        seed_usage_unit: data.seed_usage_unit.value
      }),
      ...(isPresent(data.plantingMethod) && {
        method: data.plantingMethod.value
      })
    }),
    ...(data.activityType?.canonical_name === 'inspection' && {
      rating: data.rating || null,
      ...(isPresent(data.estimated_area_loss) && {
        estimated_area_loss: convertAreaByUnitToSqm(data.estimated_area_loss, unit)
      }),
      ...(isPresent(data.estimated_production) && {
        estimated_production: numberFormatter(data.estimated_production) * reverseWeightUnitFactor
      }),
      ...(data.cause_of_loss && {
        cause_of_loss: data.cause_of_loss.value
      })
    }),
    ...(data.activityType?.canonical_name === 'fertilization' && formatFertilizationExtraForAPI(data, unit)),
    ...(data.activityType?.canonical_name === 'soil_observation' && {
      soil_type: data.soil_type?.value
    }),
    ...(data.activityType?.canonical_name === 'water_supply' && {
      water_regime_before_planting: data.water_regime_before_planting?.value,
      water_regime_while_planting: data.water_regime_while_planting?.value
    }),
    ...(data.activityType?.canonical_name === 'straw_management' && {
      type: data.strawManagementType?.value,
      operation: data.strawManagementOperation?.value
    }),
    ...(data.activityType?.canonical_name === 'cover_crop_planting' && {
      type: data.coverCropType?.value
    }),
    ...(data.activityType?.canonical_name === 'harvesting' && isPresent(data.harvest) && {
      harvest: numberFormatter(data.harvest) * reverseWeightUnitFactor
    }),
    ...(data.activityType?.canonical_name === 'land_preparation' && {
      method: data.landPreparationMethod?.value
    }),
    ...(data.activityType?.canonical_name === 'pest_disease' && {
      method: data.pestDiseaseMethod?.value,
      type: data.pestDiseaseType?.value,
      product_name: data.pestDiseaseProductName
    }),
    ...(data.activityType?.canonical_name === 'straw_incorporation' && {
      timing_days_before_planting: data.timingDaysBeforePlanting?.value
    })
  }
});
export const formatActivityForForm = (activity: IActivity, t: (k: string, options?: object) => string, unit: string, weightUnitFactor: number) => ({
  farm: {
    label: activity.farm.name,
    value: activity.farm.id
  },
  ...(activity.cropzone?.crop && {
    cropzone: {
      label: formatCropName(activity.cropzone.crop),
      value: activity.cropzone.id,
      farmId: activity.farm.id,
      cropzoneArea: activity.cropzone.area,
      cropzoneLabel: activity.cropzone.name,
      cropName: activity.cropzone.crop.rawName
    }
  }),
  activityType: {
    label: t(`activityTypes:${activity.activityType!.canonical_name}.name`, {
      defaultValue: activity.activityType!.canonical_name
    }),
    value: activity.activityType!.id,
    canonical_name: activity.activityType!.canonical_name
  },
  ...(activity.labels[0] && {
    status: {
      label: t(activity.labels[0].name),
      value: activity.labels[0].id,
      canonical: activity.labels[0].name
    }
  }),
  datetime: activity.datetime.toISO(),
  endDatetime: activity.endDatetime?.toISO(),
  assignee: activity.assigneeOption,
  ...(activity.note && {
    note: activity.note
  }),
  // inspection
  ...(isPresent(activity.estimatedProduction) && {
    estimated_production: (parseFloat(activity.estimatedProduction) * weightUnitFactor).toString()
  }),
  ...(isPresent(activity.estimatedAreaLoss) && {
    estimated_area_loss: convertSqmToAreaByUnit(activity.estimatedAreaLoss, unit)
  }),
  ...(isPresent(activity.causeOfLoss) && {
    cause_of_loss: activity.causeOfLossOption
  }),
  ...(isPresent(activity.rating) && {
    rating: activity.rating || 0
  }),
  // fertilization
  ...(isPresent(activity.activityType?.canonical_name === 'fertilization') && {
    fertilizerMethod: activity.fertilizerMethodOption,
    fertilizerType: activity.fertilizerTypeOption
  }),
  // straw management
  ...(isPresent(activity.activityType?.canonical_name === 'straw_management') && {
    strawManagementOperation: activity.strawManagementOperationOption,
    strawManagementType: activity.strawManagementTypeOption
  }),
  ...(isPresent(activity.fertilizerNitrogen) && {
    nitrogen: activity.fertilizerNitrogen
  }),
  ...(isPresent(activity.fertilizerPhosphorus) && {
    phosphorus: activity.fertilizerPhosphorus
  }),
  ...(isPresent(activity.fertilizerPotassium) && {
    potassium: activity.fertilizerPotassium
  }),
  ...(isPresent(activity.fertilizerAmount) && {
    fertilizerAmount: convertSqmToAreaByUnit(activity.fertilizerAmount, unit)
  }),
  ...(isPresent(activity.fertilizerUnit) && {
    fertilizerAmount_unit: {
      kg: {
        label: `${t('kg')}/${t(unit)}`,
        value: 'kg'
      },
      l: {
        label: `${t('liters')}/${t(unit)}`,
        value: 'L'
      }
    }[activity?.fertilizerUnit?.toLowerCase() || 'kg']
  }),
  // soil
  ...(activity.activityType?.canonical_name === 'soil_observation' && {
    soil_type: activity?.soilOption
  }),
  // harvest
  ...(isPresent(activity.activityType?.canonical_name === 'harvesting') && activity.actualHarvest && {
    harvest: activity.actualHarvest.toString()
  }),
  // planting
  ...(activity.activityType?.canonical_name === 'planting' && {
    ...(isPresent(activity.amountOfSeeds) && {
      seed_usage: Number(activity.amountOfSeeds),
      seed_usage_computed: activity.amountOfSeedsComputed
    }),
    ...(isPresent(activity.amountOfSeeds_unit) && {
      seed_usage_unit: seedUnitList[activity.amountOfSeeds_unit]
    }),
    ...(isPresent(activity.plantingMethod) && {
      plantingMethod: activity.plantingMethodOption
    })
  }),
  // land preparation
  ...(isPresent(activity.activityType?.canonical_name === 'land_preparation') && {
    landPreparationMethod: activity.landPreparationMethodOption
  }),
  // pest disease
  ...(isPresent(activity.activityType?.canonical_name === 'pest_disease') && {
    pestDiseaseMethod: activity.pestDiseaseMethodOption,
    pestDiseaseType: activity.pestDiseaseTypeOption,
    pestDiseaseProductName: activity.pestDiseaseProductName
  }),
  // straw incorporation
  ...(isPresent(activity.activityType?.canonical_name === 'straw_incorporation') && {
    timingDaysBeforePlanting: activity.timingDaysBeforePlantingOption
  }),
  ...(activity.activityType?.canonical_name === 'water_supply' && {
    water_regime_before_planting: {
      label: activity?.waterRegimeBeforePlanting,
      value: activity?.waterRegimeBeforePlanting
    },
    water_regime_while_planting: {
      label: activity?.waterRegimeWhilePlanting,
      value: activity?.waterRegimeWhilePlanting
    }
  }),
  ...(activity.activityType?.canonical_name === 'cover_crop_planting' && {
    coverCropType: activity?.coverCropPlantingOption
  }),
  timezone: activity.cropzone?.timezone,
  activityId: activity.id,
  ...(isPresent(activity.attachments) && {
    attachments: activity.attachments.map(a => ({
      id: a.id,
      url: a.url
    }))
  })
});
export const removeEmptyExtra = (obj: {
  extra?: object;
}) => {
  if (obj.extra && Object.keys(obj.extra).length === 0) {
    const {
      extra,
      ...rest
    } = obj;
    return rest;
  }
  return obj;
};
export const degreesToCardinal = (degrees: number) => {
  if (degrees < 0 || degrees >= 360) {
    degrees = (degrees % 360 + 360) % 360; // Normalize degrees to be within 0-359
  }
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'N'];
  const index = Math.round(degrees / 45); // 360/8 = 45 degrees per direction
  return directions[index];
};
const iconMap: {
  [key: string]: string;
} = {
  sunny: 'sunny',
  hot: 'sunny',
  clear: 'sunny',
  mostly_sunny: 'partly_sunny',
  partly_sunny: 'partly_sunny',
  intermittent_clouds: 'partly_sunny',
  hazy_sunshine: 'partly_sunny',
  mostly_clear: 'partly_sunny',
  partly_cloudy: 'partly_sunny',
  mostly_cloudy: 'mostly_cloudy',
  cloudy: 'cloudy',
  dreary: 'cloudy',
  fog: 'cloudy',
  hazy_moonlight: 'mostly_cloudy_night',
  windy: 'windy',
  showers: 'showers',
  mostly_cloudy_with_showers: 'partly_sunny_with_showers',
  partly_cloudy_with_showers: 'partly_sunny_with_showers',
  rain: 'rain',
  freezing_rain: 'rain',
  rain_and_snow: 'rain',
  t_storms: 't_storms',
  mostly_cloudy_with_t_storms: 'mostly_cloudy_with_t_storms',
  partly_sunny_with_t_storms: 'mostly_cloudy_with_t_storms',
  flurries: 'snow',
  ice: 'snow',
  sleet: 'snow',
  cold: 'snow',
  mostly_cloudy_with_flurries: 'cloudy_snow',
  partly_sunny_with_flurries: 'cloudy_snow',
  mostly_cloudy_with_snow: 'cloudy_snow',
  snow: 'heavy_snow'
};
export const getIconByCanonicalName = (canonical_name: string) => iconMap[canonical_name] || 'cloudy';
export const hasTodayInDataRange = (data: WeatherData[], today: DateTime) => data.some(entry => {
  const entryDate = DateTime.fromISO(entry.read_at, {
    zone: today.zone
  });
  return entryDate.hasSame(today, 'day');
});
export const calculateDateDiff = (startDateStr: string | null | undefined, endDateStr: string | null | undefined, units: Array<'years' | 'months' | 'weeks' | 'days' | 'hours' | 'minutes' | 'seconds'> = ['days'], format: string = 'd') => {
  if (startDateStr && endDateStr) {
    const startDate = DateTime.fromISO(startDateStr);
    const endDate = DateTime.fromISO(endDateStr);
    if (startDate.isValid && endDate.isValid) {
      return endDate.diff(startDate, units).toFormat(format);
    }
  }
  return null;
};
export const getIncome = ({
  income,
  unit,
  conversionMap = {
    satang: 100
  }
}: {
  unit: string | null | undefined;
  income: number | null | undefined;
  conversionMap?: Record<string, number>;
}) => {
  if (income == null) return null;
  if (unit && conversionMap[unit]) {
    return income / conversionMap[unit];
  }
  return income;
};
const emailChecker = emailMisspelled({
  domains: top100
});
export const checkEmailDomain = (email: string): string | null => {
  if (!email || !email.includes('@')) return null;
  const suggestion = emailChecker(email);
  return suggestion?.[0]?.suggest || null;
};